import React, { createContext, useContext, useState } from "react";

import {
  getIndividualAccounts,
  getIndividualDetails,
  getIndividualLoans,
  getIndividualSavings,
  getIndividualSavingsStats,
  getIndividualTransactionLimitSettings,
  getIndividualsStats,
} from "api/individual-user-management";
import {
  IGetIndividualStatsPayload,
  IGetIndividualTransactionLimitSettingsPayload,
  IIndividualAccount,
  IIndividualAccountsData,
  IIndividualDetails,
  IIndividualLoansPayload,
  IIndividualLoansSummary,
  IIndividualSavingsPayload,
  IIndividualSavingsResult,
  IIndividualSavingsStats,
  IIndividualSavingsStatsPayload,
  IIndividualStatsData,
  IIndividualTransactionLimitSettings,
  IUserDetailsData,
} from "api/individual-user-management/types";
import { START_INDEX } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";

import {
  IGetIndividualAccountsPayload,
  IIndividualsContextProps,
  IIndividualsProviderProps,
} from "./types";

export const DEFAULT_INDIVIDUALS_STATS: IIndividualStatsData = {
  active: 0,
  inActive: 0,
  total: 0,
  deactivated: 0,
};

const DEFAULT_BAL = 0;
const DEFAULT_INDIVIDUAL_ACCOUNTS_DATA: IIndividualAccountsData = {
  totalBalance: DEFAULT_BAL,
  total: 0,
  data: [],
};

const IndividualsContext = createContext<IIndividualsContextProps>({
  individualsTableHeaderCTAWidth: START_INDEX,
  handleUpdateIndividualsTableHeaderCTAWidth: () => {
    return null;
  },
  isLoadingIndividualsStats: false,
  setIsLoadingIndividualsStats: () => {
    return null;
  },
  individualsStats: DEFAULT_INDIVIDUALS_STATS,
  setIndividualsStats: () => {
    return null;
  },
  handleGetIndividualsStats: async (_: IGetIndividualStatsPayload) => {
    // default
  },
  individualDetails: null,
  setIndividualDetails: () => {
    return null;
  },
  isLoadingIndividualDetails: false,
  setIsLoadingIndividualDetails: () => {
    return null;
  },

  handleGetIndividualDetails: async (_: string) => {
    return {};
  },
  individualAccountsData: DEFAULT_INDIVIDUAL_ACCOUNTS_DATA,
  setIndividualAccountsData: () => {
    return null;
  },
  currentAccountDetails: null,
  setCurrentAccountDetails: () => {
    return null;
  },
  isLoadingIndividualAccounts: false,
  handleGetIndividualAccounts: async (_: IGetIndividualAccountsPayload) => {
    // do nothing
  },
  isLoadingIndividualLoans: false,
  setIsLoadingIndividualLoans: () => {
    return null;
  },
  individualLoansResult: null,
  setIndividualLoansResult: () => {
    return null;
  },
  handleGetIndividualLoans: async (_: IIndividualLoansPayload) => {
    // do nothing
  },
  isLoadingIndividualSavingStats: false,
  setIsLoadingIndividualSavingsStats: () => {
    return null;
  },
  individualSavingsResultStats: null,
  setIndividualSavingsResultStats: () => {
    return null;
  },
  handleGetIndividualSavingsStats: async (
    _: IIndividualSavingsStatsPayload
  ) => {
    // do nothing
  },
  isLoadingIndividualSavings: false,
  setIsLoadingIndividualSavings: () => {
    return null;
  },
  individualSavingsResult: null,
  setIndividualSavingsResult: () => {
    return null;
  },
  handleGetIndividualSavings: async (_: IIndividualSavingsPayload) => {
    // do nothing
  },
  isLoadingIndividualTransactionLimitSettings: false,
  individualTransactionLimitSettings: null,
  handleGetIndividualTransactionLimitSettings: async (
    _: IGetIndividualTransactionLimitSettingsPayload
  ) => {
    // do nothing
  },
  userDetailsData: { email: "", phoneNumber: "" },
  setUserDetailsData: () => {
    return null;
  },
});

export const IndividualsProvider = (props: IIndividualsProviderProps) => {
  const { children } = props;

  const [individualsTableHeaderCTAWidth, setIndividualsTableHeaderCTAWidth] =
    useState<number>(START_INDEX);
  const [isLoadingIndividualsStats, setIsLoadingIndividualsStats] =
    useState(false);
  const [individualsStats, setIndividualsStats] =
    useState<IIndividualStatsData>(DEFAULT_INDIVIDUALS_STATS);
  const [individualDetails, setIndividualDetails] =
    useState<IIndividualDetails | null>(null);
  const [isLoadingIndividualDetails, setIsLoadingIndividualDetails] =
    useState(false);
  const [isLoadingIndividualAccounts, setIsLoadingIndividualAccounts] =
    useState(false);
  const [individualAccountsData, setIndividualAccountsData] =
    useState<IIndividualAccountsData | null>(DEFAULT_INDIVIDUAL_ACCOUNTS_DATA);
  const [isLoadingIndividualLoans, setIsLoadingIndividualLoans] =
    useState(false);
  const [individualLoansResult, setIndividualLoansResult] =
    useState<IIndividualLoansSummary | null>(null);
  const [isLoadingIndividualSavingStats, setIsLoadingIndividualSavingsStats] =
    useState(false);
  const [individualSavingsResultStats, setIndividualSavingsResultStats] =
    useState<IIndividualSavingsStats | null>(null);
  const [isLoadingIndividualSavings, setIsLoadingIndividualSavings] =
    useState(false);
  const [individualSavingsResult, setIndividualSavingsResult] =
    useState<IIndividualSavingsResult | null>(null);
  const [
    isLoadingIndividualTransactionLimitSettings,
    setIsLoadingIndividualTransactionLimitSettings,
  ] = useState(false);
  const [
    individualTransactionLimitSettings,
    setIndividualTransactionLimitSettings,
  ] = useState<IIndividualTransactionLimitSettings | null>(null);
  const [currentAccountDetails, setCurrentAccountDetails] =
    useState<IIndividualAccount | null>(null);
  const [userDetailsData, setUserDetailsData] = useState<IUserDetailsData>({
    email: "",
    phoneNumber: "",
  });

  const handleUpdateIndividualsTableHeaderCTAWidth = (value: number) => {
    setIndividualsTableHeaderCTAWidth(value);
  };

  const handleGetIndividualsStats = async (
    payload: IGetIndividualStatsPayload
  ) => {
    setIsLoadingIndividualsStats(true);
    try {
      const { error, message, data } = await getIndividualsStats(payload);
      if (error) {
        customErrorToast({ message });
      } else {
        setIndividualsStats(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingIndividualsStats(false);
  };

  const handleGetIndividualDetails = async (individualId: string) => {
    setIsLoadingIndividualDetails(true);
    let error;
    let data;
    let message;
    try {
      const {
        error: apiError,
        data: apiData,
        message: apiMessage,
      } = await getIndividualDetails({
        userId: individualId,
      });
      if (apiError) {
        customErrorToast({
          message: apiMessage,
        });
      } else {
        setIndividualDetails(apiData);
      }
      error = apiError;
      message = apiMessage;
      data = apiData;
    } catch (err) {
      console.error("Error fetching individual details:", err);
    }
    setIsLoadingIndividualDetails(false);
    return { error, data, message };
  };

  const handleGetIndividualAccounts = async (
    payload: IGetIndividualAccountsPayload
  ) => {
    const { userId, isRefetching } = payload;
    let id = "";
    if (userId && userId !== "") {
      id = userId;
    } else if (individualDetails) {
      id = individualDetails?.id;
    } else {
      console.error("Individual absent!");
      return;
    }

    if (!isRefetching) {
      setIsLoadingIndividualAccounts(true);
    }
    try {
      const { error, message, data } = await getIndividualAccounts({
        ...payload,
        userId: id,
      });
      if (error) {
        customErrorToast({ message });
      } else {
        setIndividualAccountsData(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetching) {
      setIsLoadingIndividualAccounts(false);
    }
  };

  const handleGetIndividualLoans = async (
    payload: IIndividualLoansPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    // const { isRefetching } = others;
    const refetch = others?.isRefetching;
    try {
      if (!refetch) {
        setIsLoadingIndividualLoans(true);
      }
      const { error, message, data } = await getIndividualLoans(payload);
      if (error) {
        console.error("message::", message, error);
      } else {
        setIndividualLoansResult(data);
      }
      setIsLoadingIndividualLoans(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!refetch) {
      setIsLoadingIndividualLoans(false);
    }
  };

  const handleGetIndividualSavingsStats = async (
    payload: IIndividualSavingsStatsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    const isRefetch = others?.isRefetching;
    try {
      if (!isRefetch) {
        setIsLoadingIndividualSavingsStats(true);
      }
      const { error, message, data } = await getIndividualSavingsStats(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualSavingsResultStats(data);
      }
      setIsLoadingIndividualSavingsStats(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetch) {
      setIsLoadingIndividualSavingsStats(false);
    }
  };

  const handleGetIndividualSavings = async (
    payload: IIndividualSavingsPayload,
    others?: {
      isRefetching: boolean;
    }
  ) => {
    // const { isRefetching } = others;
    const refetch = others?.isRefetching;
    try {
      if (!refetch) {
        setIsLoadingIndividualSavings(true);
      }
      const { error, message, data } = await getIndividualSavings(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualSavingsResult(data);
      }
      setIsLoadingIndividualSavings(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!refetch) {
      setIsLoadingIndividualSavings(false);
    }
  };

  const handleGetIndividualTransactionLimitSettings = async (
    payload: IGetIndividualTransactionLimitSettingsPayload
  ) => {
    setIsLoadingIndividualTransactionLimitSettings(true);

    try {
      const { error, message, data } =
        await getIndividualTransactionLimitSettings(payload);
      if (error) {
        customErrorToast({ message });
      } else {
        setIndividualTransactionLimitSettings(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingIndividualTransactionLimitSettings(false);
  };

  return (
    <IndividualsContext.Provider
      value={{
        individualsTableHeaderCTAWidth,
        handleUpdateIndividualsTableHeaderCTAWidth,
        individualsStats,
        setIndividualsStats,
        isLoadingIndividualsStats,
        setIsLoadingIndividualsStats,
        handleGetIndividualsStats,
        individualDetails,
        setIndividualDetails,
        isLoadingIndividualDetails,
        setIsLoadingIndividualDetails,
        handleGetIndividualDetails,
        individualAccountsData: individualAccountsData!,
        setIndividualAccountsData,
        currentAccountDetails,
        setCurrentAccountDetails,
        isLoadingIndividualAccounts,
        handleGetIndividualAccounts,
        handleGetIndividualLoans,
        isLoadingIndividualLoans,
        setIsLoadingIndividualLoans,
        individualLoansResult,
        setIndividualLoansResult,
        handleGetIndividualSavings,
        isLoadingIndividualSavings,
        setIsLoadingIndividualSavings,
        individualSavingsResult,
        setIndividualSavingsResult,
        handleGetIndividualSavingsStats,
        isLoadingIndividualSavingStats,
        setIsLoadingIndividualSavingsStats,
        individualSavingsResultStats,
        setIndividualSavingsResultStats,
        isLoadingIndividualTransactionLimitSettings,
        individualTransactionLimitSettings,
        handleGetIndividualTransactionLimitSettings,
        userDetailsData,
        setUserDetailsData,
      }}
    >
      {children}
    </IndividualsContext.Provider>
  );
};

export const useIndividuals = () => {
  const context = useContext(IndividualsContext);
  if (context === undefined) {
    throw new Error("useIndividuals must be used within a IndividualsProvider");
  }
  return context;
};
